import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeIcon from '@mui/icons-material/Merge';

import {
  activeAdminTenantId,
  canOperateTenant,
  canWriteTenant,
  isEffectiveMembership,
  useAuth,
} from '../auth/AuthContext';
import { LiveSignalWall } from '../components/LiveSignalWall';
import { LiveSessionTile } from '../components/LiveSessionTile';
import { StandbyGoBanner } from '../components/StandbyGoBanner';
import { MyLiveWall } from './MyLiveWall';
import * as liveWallApi from '../api/liveWall';
import { listLiveSessions, type LiveSessionSummary } from '../api/live';
import { listProcedures, type Procedure } from '../api/procedures';
import { listParticipants, type Participant } from '../api/tenants';

/**
 * Live Recording (LIVE-OR-STREAMING). Every case a tenant is recording right
 * now, each as its own section: a case's concurrently-recording participants
 * are overlaid on shared axes (with automatic divergence markers); unrelated
 * cases (different procedures) sit in separate sections. A finished session
 * drops off on its own.
 *
 * A "case" here is inferred server-side as tenant + procedure, which is
 * deliberately coarse: two sessions that merely happen to run the same
 * procedure at the same time — a simulation alongside the real thing — are
 * overlaid as if they were one team. **Split** (LIVE-SPLIT-PARTICIPANT) is the
 * way out of that mid-session: it moves one participant onto their own wall,
 * for everyone watching, until it's merged back.
 */
export function LiveRecording() {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const tenantName =
    user?.memberships.find((m) => m.tenant_id === tenantId)?.tenant_name ??
    null;

  const [cases, setCases] = useState<liveWallApi.LiveCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [procs, setProcs] = useState<Procedure[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sessions, setSessions] = useState<LiveSessionSummary[]>([]);
  const [drill, setDrill] = useState<LiveSessionSummary | null>(null);

  // participant user id → their in-progress session, for the drill-down.
  const sessionByParticipant = useMemo(() => {
    const m = new Map<string, LiveSessionSummary>();
    for (const s of sessions) {
      if (s.participant_user_id) m.set(s.participant_user_id, s);
    }
    return m;
  }, [sessions]);

  const procName = useMemo(() => {
    const m = new Map(procs.map((p) => [p.id, p.name]));
    return (id: string | null) => (id ? (m.get(id) ?? null) : null);
  }, [procs]);

  const participantName = useMemo(() => {
    const m = new Map(participants.map((p) => [p.id, p]));
    return (id: string) => {
      const p = m.get(id);
      return p ? p.full_name || p.email || 'Participant' : id.slice(0, 8);
    };
  }, [participants]);

  useEffect(() => {
    if (!tenantId) return;
    void listProcedures(tenantId).then(setProcs).catch(() => setProcs([]));
    void listParticipants(tenantId)
      .then(setParticipants)
      .catch(() => setParticipants([]));
  }, [tenantId]);

  const canWrite = canWriteTenant(user, tenantId);
  // Guards double-clicks while the request is in flight and, more usefully,
  // stops the 3 s poll from repainting a stale roster over the change.
  const [splitting, setSplitting] = useState<string | null>(null);

  const splitOut = async (caseId: string, participantId: string) => {
    if (!tenantId) return;
    setSplitting(participantId);
    try {
      await liveWallApi.splitLiveParticipant(tenantId, caseId, participantId);
      setCases(await liveWallApi.listLiveCases(tenantId));
      setError(null);
    } catch {
      setError('Could not split that participant out. Please try again.');
    } finally {
      setSplitting(null);
    }
  };

  const mergeBack = async (caseId: string, participantId: string) => {
    if (!tenantId) return;
    setSplitting(participantId);
    try {
      await liveWallApi.mergeLiveParticipant(tenantId, caseId, participantId);
      setCases(await liveWallApi.listLiveCases(tenantId));
      setError(null);
    } catch {
      setError('Could not merge that participant back. Please try again.');
    } finally {
      setSplitting(null);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const load = () => {
      void liveWallApi
        .listLiveCases(tenantId)
        .then((cs) => {
          if (!cancelled) {
            setCases(cs);
            setError(null);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load live cases.');
        });
      // The in-progress sessions carry participant_user_id → session, so a
      // click on a participant can open their detail.
      void listLiveSessions(tenantId)
        .then((ss) => {
          if (!cancelled) setSessions(ss);
        })
        .catch(() => undefined);
    };
    void load();
    const t = setInterval(() => void load(), 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [tenantId]);

  if (!tenantId) {
    // A participant watches the cases they are in (LIVE-WALL-PARTICIPANT).
    if (
      user?.memberships.some(
        (m) => m.role === 'participant' && isEffectiveMembership(m),
      )
    ) {
      return <MyLiveWall />;
    }
    return (
      <Alert severity="warning">
        You don&rsquo;t hold an active tenant_admin role. Sign in as a tenant
        admin to watch that tenant&rsquo;s live recordings.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Live Recording</Typography>
        <Typography variant="body2" color="text.secondary">
          Every case recording right now — a team&rsquo;s participants overlaid
          on shared axes, with a marker the instant their signals diverge.
        </Typography>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Two-stage start: recordings that are running but not yet started
          (ROLES-AND-GO). In the OR nobody touches the phone, so Go can be
          pressed here. */}
      {sessions
        .filter((s) => s.standby)
        .map((s) => (
          <StandbyGoBanner
            key={s.session_id}
            tenantId={tenantId}
            session={s}
            canGo={canOperateTenant(user, tenantId)}
          />
        ))}

      {cases === null ? (
        <CircularProgress />
      ) : cases.length === 0 ? (
        <Alert severity="info">
          Nothing recording right now. Start a session on a capture device (with
          a procedure and participant assigned) and it appears here within a few
          seconds.
        </Alert>
      ) : (
        <Stack spacing={4} divider={<Divider flexItem />}>
          {cases.map((c) => {
            const isSplit = !!c.split_of;
            return (
              <Stack key={c.case_id} spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
                  <Typography variant="h6">
                    {procName(c.procedure_id) ?? 'Procedure'}
                  </Typography>
                  <Chip
                    size="small"
                    color={isSplit ? 'default' : 'success'}
                    variant={isSplit ? 'outlined' : 'filled'}
                    label={
                      isSplit
                        ? 'Split out'
                        : `${c.participant_ids.length} live`
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {c.participant_ids.length === 0
                      ? 'Waiting for participants…'
                      : isSplit
                        ? 'Own track — not overlaid with the others'
                        : null}
                    {tenantName ? ` · ${tenantName}` : ''}
                  </Typography>
                </Stack>

                {/* Per-participant roster with the split control. The names
                    used to be a plain comma-joined line; they carry the action
                    now, so separating someone is where you see they're in the
                    overlay. */}
                {c.participant_ids.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {c.participant_ids.map((pid) => {
                      const name = participantName(pid);
                      const busy = splitting === pid;
                      if (isSplit) {
                        return (
                          <Chip
                            key={pid}
                            size="small"
                            variant="outlined"
                            label={name}
                            onClick={() => {
                              const s = sessionByParticipant.get(pid);
                              if (s) setDrill(s);
                            }}
                          />
                        );
                      }
                      return (
                        <Chip
                          key={pid}
                          size="small"
                          variant="outlined"
                          label={name}
                          disabled={busy}
                          onClick={() => {
                            const s = sessionByParticipant.get(pid);
                            if (s) setDrill(s);
                          }}
                          onDelete={
                            canWrite
                              ? () => void splitOut(c.case_id, pid)
                              : undefined
                          }
                          deleteIcon={
                            canWrite ? (
                              <Tooltip title={`Give ${name} their own track — they are not part of this case`}>
                                <CallSplitIcon
                                  aria-label={`split ${name} out of this case`}
                                />
                              </Tooltip>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </Stack>
                )}

                {isSplit && canWrite && c.split_participant_id && (
                  <Box>
                    <Button
                      size="small"
                      startIcon={<MergeIcon />}
                      disabled={splitting === c.split_participant_id}
                      onClick={() =>
                        void mergeBack(
                          c.case_id,
                          c.split_participant_id as string,
                        )
                      }
                    >
                      Merge back into{' '}
                      {procName(c.procedure_id) ?? 'the shared case'}
                    </Button>
                  </Box>
                )}
                <LiveSignalWall
                  tenantId={tenantId}
                  caseId={c.case_id}
                  participantLabel={participantName}
                  onParticipantClick={(pid) => {
                    const s = sessionByParticipant.get(pid);
                    if (s) setDrill(s);
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={drill !== null}
        onClose={() => setDrill(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {drill?.participant_label ?? 'Participant'} · live detail
          <IconButton onClick={() => setDrill(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {drill && (
            <LiveSessionTile
              key={drill.session_id}
              scope={{ tenantId }}
              session={drill}
              variant="solo"
              onEnded={() => setDrill(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
